<script>
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import EventsItem from './EventsItem.vue';
import EventsMap from './EventsMap.vue';
import Breadcrumbs from './util/Breadcrumbs.vue';
import Pagination from './util/Pagination.vue';
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import EventTypeSelect from './util/EventTypeSelect.vue';
import AppSelect from '../shared/AppSelect.vue';

export default {
  setup() {
    const i18n = useI18n();
    return {
      t: i18n.t,
      locale: i18n.locale
    };
  },
        data() {
        return {
          country: 'all',
          city: 'all',
          eventType: 'all',
          startsAt: new Date().getTime(), // Set default to current date
          events: [],
          availableCountries: [],
          availableCities: [],
          availableEventTypes: [],
          isLoading: false,
          isLoadingCities: false,
          isLoadingEvents: false,
          isDatePickerOpen: false,
          debounceTimer: null,
          userPermissions: {
            can_create: false,
            can_manage: false
          },
          isLoadingPermissions: false,
          // Pagination state
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          perPage: 10,
        }
      },
  components: {
    'events-item': EventsItem,
    'events-map': EventsMap,
    'breadcrumbs': Breadcrumbs,
    'pagination': Pagination,
    'vue-date-picker': VueDatePicker,
    'event-type-select': EventTypeSelect,
    AppSelect,
  },
  methods: {
            getList() {
      // Clear any existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Debounce the API call to prevent rapid successive calls
      this.debounceTimer = setTimeout(() => {
        this.isLoadingEvents = true;
        const url = "/api/v1/events";
        let params = {};

        // Build filter object only if we have filters
        const hasFilters = this.country || this.city || this.eventType || this.startsAt;

        if (hasFilters) {
          params.filter = {};

          if (this.country && this.country !== 'all') {
            params.filter.country = this.country;
          }

          if (this.city && this.city !== 'all' && this.country && this.country !== 'all') {
            params.filter.city = this.city;
          }

          if (this.eventType && this.eventType !== 'all') {
            params.filter.event_type = parseInt(this.eventType);
          }

          if (this.startsAt) {
            // Format date as YYYY-MM-DD for API
            const date = new Date(this.startsAt);
            params.filter.start_time = date.toISOString().split('T')[0];
          }
        }

        // Add pagination parameters
        params.page = this.currentPage;
        params.per_page = this.perPage;

        axios.get(url, { params: params })
          .then(response => {
            // Backend responses may vary; ensure `events` is always an Array
            const payload = response?.data;
            const list = payload?.data ?? payload;
            this.events = Array.isArray(list) ? list : [];

            // Update pagination metadata
            if (payload?.meta?.pagination) {
              const pagination = payload.meta.pagination;
              this.currentPage = pagination.current_page;
              this.totalPages = pagination.total_pages;
              this.totalCount = pagination.total_count;
              this.perPage = pagination.per_page;
            }
          })
          .catch(error => {
            this.events = [];
            // Reset pagination on error
            this.currentPage = 1;
            this.totalPages = 1;
            this.totalCount = 0;
          })
          .finally(() => {
            this.isLoadingEvents = false;
          });
      }, 300); // 300ms debounce
    },

    getCountries() {
      const url = "/api/v1/events/countries";
      axios.get(url, {
        headers: {
          'Accept-Language': (navigator.language || 'ru').split('-')[0] || 'ru'
        }
      })
        .then(response => {
          const countries = response?.data?.countries;
          this.availableCountries = Array.isArray(countries) ? countries : [];
        })
        .catch(error => {
          this.availableCountries = [];
        });
    },

    getEventTypes() {
      const url = "/api/dict/v1/event_types";
      const currentLocale = (this.locale && this.locale.value) ? this.locale.value : 'ru';
      axios.get(url, {
        headers: {
          'Accept-Language': currentLocale
        }
      })
        .then(response => {
          const types = response?.data;
          this.availableEventTypes = Array.isArray(types) ? types : [];
        })
        .catch(error => {
          this.availableEventTypes = [];
        });
    },

    getUserPermissions() {
      this.isLoadingPermissions = true;
      const url = "/api/v1/events/permissions";
      axios.get(url)
        .then(response => {
          this.userPermissions = response.data;
        })
        .catch(error => {
          // Default to no permissions on error
          this.userPermissions = {
            can_create: false,
            can_manage: false
          };
        })
        .finally(() => {
          this.isLoadingPermissions = false;
        });
    },

    getCities(country) {
      if (!country || country === 'all') {
        this.availableCities = [];
        return;
      }

      this.isLoadingCities = true;
      const url = "/api/v1/events/cities";
      axios.get(url, { params: { country: country } })
        .then(response => {
          const cities = response?.data?.cities;
          this.availableCities = Array.isArray(cities) ? cities : [];
        })
        .catch(error => {
          this.availableCities = [];
        })
        .finally(() => {
          this.isLoadingCities = false;
        });
    },

            onCountryChange() {
      // Reset city when country changes
      this.city = 'all';
      this.availableCities = [];
      this.currentPage = 1; // Reset to first page when changing filters

      // Use nextTick to ensure DOM updates are complete
      this.$nextTick(() => {
        // Load cities for the new country (only if not "all")
        if (this.country && this.country !== 'all') {
          this.getCities(this.country);
        }

        // Update events list
        this.getList();
      });
    },

    onCityChange() {
      this.currentPage = 1; // Reset to first page when changing filters
      this.$nextTick(() => {
        this.getList();
      });
    },

    onDateChange(newDate) {
      this.currentPage = 1; // Reset to first page when changing filters
      this.$nextTick(() => {
        this.getList();
      });
    },

    onEventTypeChange() {
      this.currentPage = 1; // Reset to first page when changing filters
      this.$nextTick(() => {
        this.getList();
      });
    },



    clearFilters() {
      this.country = 'all';
      this.city = 'all';
      this.eventType = 'all';
      this.startsAt = new Date().getTime(); // Reset to current date instead of null
      this.availableCities = [];
      this.currentPage = 1; // Reset to first page when clearing filters
      this.$nextTick(() => {
        this.getList();
      });
    },

    join(event) {
      const url = "/api/v1/events/join";
      axios.post(url, { event_id: event.id } )
        .then(response => {
          event.attributes.joinable = false;
          event.attributes.joined = true;
        })
        .catch(error => {
          console.log(error);
        });
    },

    handleEventUpdated(updatedEvent) {
      // Find and update the event in the events array
      const index = this.events.findIndex(event => event.id === updatedEvent.id);
      if (index !== -1) {
        this.events.splice(index, 1, updatedEvent);
      }
    },

    // Pagination methods
    onPageChange(page) {
      this.currentPage = page;
      this.getList();
    },

    onPerPageChange(perPage) {
      this.perPage = perPage;
      this.currentPage = 1; // Reset to first page when changing per page
      this.getList();
    },
  },
  computed: {
    cityPlaceholder() {
      if (this.isLoadingCities) {
        return this.t('events.loading');
      }
      if (!this.country || this.country === 'all') {
        return this.t('events.select_country');
      }
      return this.t('events.all_cities');
    },
    hasEventsWithCoordinates() {
      return this.events.some(event =>
        event.attributes.latitude &&
        event.attributes.longitude &&
        parseFloat(event.attributes.latitude) !== 0 &&
        parseFloat(event.attributes.longitude) !== 0
      );
    },
    countryOptions() {
      return [
        { value: 'all', label: this.t('events.all_countries') },
        ...this.availableCountries.map((country) => ({
          value: country.code,
          label: country.name,
        })),
      ];
    },
    cityOptions() {
      return [
        { value: 'all', label: this.cityPlaceholder },
        ...this.availableCities.map((cityName) => ({
          value: cityName,
          label: cityName,
        })),
      ];
    },
    eventTypeFilterOptions() {
      return [
        { value: 'all', label: this.t('events.all_types') },
        ...this.availableEventTypes.map((type) => ({
          value: type.id,
          label: type.name,
        })),
      ];
    },
  },
  watch: {
    'locale.value': {
      handler() {
        this.getEventTypes();
      },
      immediate: false
    }
  },
  beforeMount() {
    this.getCountries();
    this.getEventTypes();
    this.getList();
    this.getUserPermissions();
  },
  beforeDestroy() {
    // Clean up timer to prevent memory leaks
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
</script>

<template>
  <breadcrumbs />

  <p v-if="!isLoadingPermissions && userPermissions.can_create">
    <router-link :to="{ name: 'new_event' }" class="button button_wide mb-1" data-turbolinks="false">
      {{ t('events.create') }}
    </router-link>
  </p>

  <div class="filter-section" key="filter-section">
    <h4>{{ t('events.filter_title') }}</h4>
    <div class="filter-controls">
      <div class="filter-item">
        <label>{{ t('events.country') }}</label>
        <AppSelect
          v-model="country"
          variant="filter"
          :options="countryOptions"
          @change="onCountryChange"
        />
      </div>

      <div class="filter-item">
        <label>{{ t('events.city') }}</label>
        <AppSelect
          v-model="city"
          variant="filter"
          :options="cityOptions"
          :disabled="!country || country === 'all' || isLoadingCities"
          @change="onCityChange"
        />
      </div>

      <div class="filter-item">
        <label>{{ t('events.event_type') }}</label>
        <AppSelect
          v-model="eventType"
          variant="filter"
          :options="eventTypeFilterOptions"
          @change="onEventTypeChange"
        />
      </div>

      <div class="filter-item">
        <label>{{ t('events.date') }}</label>
        <vue-date-picker
          v-model="startsAt"
          @update:model-value="onDateChange"
          :enable-time-picker="false"
          format="dd/MM/yyyy"
          :placeholder="t('events.select_date')"
          :auto-apply="true"
          :close-on-auto-apply="true"
          :text-input="true"
          :clearable="true"
          :teleport="true"
          :teleport-target="'body'"
          :model-type="'timestamp'"
          class="filter-datepicker"
        >
          <template #input-icon>
            <i class="bi bi-calendar4-week"></i>
          </template>
        </vue-date-picker>
      </div>

      <div class="filter-actions">
        <button @click="clearFilters" class="btn-clear-filters">
          {{ t('events.clear_filters') }}
        </button>
        <span v-show="isLoadingEvents" class="loading-text">{{ t('events.loading') }}</span>
      </div>
    </div>
  </div>

  <!-- Events Map -->
  <!-- <events-map
    :events="events"
    :map-height="400"
    v-if="events.length > 0 && !isLoadingEvents && hasEventsWithCoordinates"
  /> -->

  <!-- No coordinates message -->
  <div v-if="events.length > 0 && !isLoadingEvents && !hasEventsWithCoordinates" class="no-coordinates">
    <p>🗺️ {{ t('events.map_unavailable') }}</p>
  </div>

  <div v-if="isLoadingEvents && events.length === 0" class="loading-container">
    <div class="loading-spinner">⏳</div>
    <p>{{ t('events.loading_events') }}</p>
  </div>

  <div v-else-if="events.length === 0" class="no-events">
    <p>{{ t('events.no_events') }}</p>
    <p v-if="(country && country !== 'all') || (city && city !== 'all') || (eventType && eventType !== 'all')" class="filter-hint">
      {{ t('events.try_filters') }} <a href="#" @click.prevent="clearFilters">{{ t('events.clear_filters_link') }}</a>
    </p>
  </div>

  <div v-if="events.length > 0 && !isLoadingEvents" class="events-list">
    <events-item
      v-for="event in events"
      :key="'event-' + event.id"
      :event="event"
      @join="join"
      @event-updated="handleEventUpdated"
    ></events-item>

    <!-- Pagination -->
    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-count="totalCount"
      :per-page="perPage"
      @page-change="onPageChange"
      @per-page-change="onPerPageChange"
    />
  </div>

</template>

<style>
/* Removed conflicting .event styles - now using .event-card in EventsItem.vue */

.filter-section {
  background-color: var(--primary-bg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  transition: var(--transition-fast);
}

.filter-section h4 {
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md) 0;
  font-size: var(--font-size-base);
  font-weight: bold;
}

.filter-controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  align-items: end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  min-width: 150px;
}

.filter-item label {
  color: var(--text-primary);
  font-weight: bold;
  font-size: var(--font-size-sm);
  margin: 0;
}

.filter-select {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background-color: var(--bg-white);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.filter-select:focus {
  outline: none;
  border-color: var(--icon-bg);
  box-shadow: 0 0 0 2px rgba(194, 156, 88, 0.2);
}

.filter-select:disabled {
  background-color: var(--gray-200);
  color: var(--text-muted);
  cursor: not-allowed;
}

/* DatePicker Styling */
.filter-datepicker {
  width: 100%;
}

.filter-datepicker .dp__input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) 40px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background-color: var(--bg-white);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.filter-datepicker .dp__input:focus {
  outline: none;
  border-color: var(--icon-bg);
  box-shadow: 0 0 0 2px rgba(194, 156, 88, 0.2);
}

.filter-datepicker .dp__input::placeholder {
  color: var(--text-primary);
  opacity: 0.7;
}

.filter-datepicker .dp__input_icon {
  color: var(--text-primary);
  padding-left: 12px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.filter-datepicker .dp__input_icon:hover {
  color: var(--hover-bg);
}

.filter-datepicker .dp__input_icon .bi {
  font-size: 16px;
  color: inherit;
}

/* Calendar Popup - High Z-Index and Proper Positioning */
.dp__main {
  z-index: 9999 !important;
  background-color: white !important;
  border: 1px solid #bd9c65 !important;
  border-radius: 4px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.dp__menu {
  z-index: 9999 !important;
  background-color: white !important;
  border: 1px solid #bd9c65 !important;
  border-radius: 4px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.dp__calendar_header {
  background-color: #c29c58 !important;
  color: #60451f !important;
  border-radius: 4px 4px 0 0 !important;
}

.dp__calendar_header button {
  color: #60451f !important;
  background: none !important;
  border: none !important;
  padding: 8px !important;
  cursor: pointer !important;
  border-radius: 4px !important;
  transition: background-color 0.2s ease !important;
}

.dp__calendar_header button:hover {
  background-color: #b08f4f !important;
}

.dp__calendar {
  background-color: white !important;
  padding: 8px !important;
}

.dp__calendar_row {
  background-color: white !important;
}

.dp__cell_inner {
  color: #60451f !important;
  background-color: transparent !important;
  border-radius: 4px !important;
  transition: all 0.2s ease !important;
}

.dp__cell_inner:hover {
  background-color: #efd8b0 !important;
  color: #60451f !important;
}

.dp__active_date {
  background-color: #c29c58 !important;
  color: #60451f !important;
  font-weight: bold !important;
}

.dp__today {
  border: 2px solid #bd9c65 !important;
  font-weight: bold !important;
}

.dp__month_year_row {
  background-color: #c29c58 !important;
  color: #60451f !important;
  padding: 8px !important;
  border-radius: 0 0 4px 4px !important;
}

.dp__month_year_select {
  background-color: white !important;
  color: #60451f !important;
  border: 1px solid #bd9c65 !important;
  border-radius: 4px !important;
  padding: 4px 8px !important;
  font-size: 14px !important;
}

.dp__month_year_select:focus {
  outline: none !important;
  border-color: #c29c58 !important;
  box-shadow: 0 0 0 2px rgba(194, 156, 88, 0.2) !important;
}

/* Clear button styling */
.dp__clear_icon {
  color: #bd9c65 !important;
  transition: color 0.2s ease !important;
}

.dp__clear_icon:hover {
  color: #dc3545 !important;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.btn-clear-filters {
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  border-radius: var(--border-radius-sm);
  background-color: var(--accent-light);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: bold;
  cursor: pointer;
  transition: var(--transition-fast);
}

.btn-clear-filters:hover {
  background-color: var(--hover-bg);
  transform: translateY(-1px);
}

.loading-text {
  color: var(--primary-text);
  font-style: italic;
  font-size: var(--font-size-sm);
  opacity: 1;
  transition: opacity 0.3s ease;
}

.loading-text:empty {
  opacity: 0;
}

.loading-container {
  text-align: center;
  padding: 40px 20px;
  color: var(--primary-text);
}

.loading-spinner {
  font-size: var(--font-size-2xl);
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.no-events {
  text-align: center;
  padding: 40px 20px;
  color: var(--primary-text);
}

.filter-hint {
  font-size: var(--font-size-sm);
  margin-top: 10px;
}

.filter-hint a {
  color: var(--icon-bg);
  text-decoration: underline;
}

.filter-hint a:hover {
  color: var(--text-primary);
}



.permissions-loading {
  text-align: center;
  padding: 10px;
  color: var(--primary-text);
  font-style: italic;
}

.permissions-loading .loading-text {
  font-size: var(--font-size-sm);
}

.no-coordinates {
  text-align: center;
  padding: 20px;
  background-color: var(--gray-100);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-lg);
  margin: 20px 0;
  color: var(--gray-700);
}

.no-coordinates p {
  margin: 0;
  font-size: var(--font-size-sm);
}

.events-list {
  display: block;
  width: 100%;
  position: relative;
  z-index: 1;
  visibility: visible;
  opacity: 1;
}

.events-list :deep(.event-card) {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  z-index: 1 !important;
}

@media (max-width: 768px) {
  .events-list {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 100% !important;
    min-height: 100px;
  }

  .events-list :deep(.event-card) {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    width: 100% !important;
    min-height: 120px !important;
    margin-bottom: var(--spacing-xl) !important;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .input-group {
    min-width: auto;
  }
}
</style>
